/**
 * Production-ready logging utility for VA Sprouts
 * Provides conditional logging based on environment and log levels
 */

import { isDevelopment, isDebugMode } from './environment.js';

// Log levels with priority
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

// Current log level based on environment
const getLogLevel = () => {
  const env = import.meta.env.MODE || 'production';
  
  switch (env) {
    case 'development':
      return LOG_LEVELS.DEBUG;
    case 'staging':
      return LOG_LEVELS.INFO;
    case 'production':
    default:
      return LOG_LEVELS.ERROR;
  }
};

const currentLogLevel = getLogLevel();

/**
 * Format log message with timestamp and context
 */
const formatMessage = (level, context, message, data = null) => {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}] [${context}]`;
  
  if (data) {
    return [prefix, message, data];
  }
  return [prefix, message];
};

/**
 * Logger class with context-aware logging
 */
class Logger {
  constructor(context = 'App') {
    this.context = context;
  }

  error(message, data = null) {
    if (currentLogLevel >= LOG_LEVELS.ERROR) {
      console.error(...formatMessage('error', this.context, message, data));
    }
  }

  warn(message, data = null) {
    if (currentLogLevel >= LOG_LEVELS.WARN) {
      console.warn(...formatMessage('warn', this.context, message, data));
    }
  }

  info(message, data = null) {
    if (currentLogLevel >= LOG_LEVELS.INFO) {
      console.info(...formatMessage('info', this.context, message, data));
    }
  }

  debug(message, data = null) {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      console.log(...formatMessage('debug', this.context, message, data));
    }
  }

  /**
   * Log performance metrics
   */
  performance(operation, startTime, endTime = Date.now()) {
    const duration = endTime - startTime;
    this.debug(`Performance: ${operation} completed in ${duration}ms`);
  }

  /**
   * Create a child logger with extended context
   */
  child(additionalContext) {
    return new Logger(`${this.context}:${additionalContext}`);
  }
}

/**
 * Create logger instances for different modules
 */
export const createLogger = (context) => new Logger(context);

// Export default loggers for common use cases
export const logger = new Logger('App');
export const apiLogger = new Logger('API');
export const authLogger = new Logger('Auth');
export const jobMatchingLogger = new Logger('JobMatching');
export const realtimeLogger = new Logger('Realtime');
export const storageLogger = new Logger('Storage');
export const analyticsLogger = new Logger('Analytics');

// Development-only debug utilities
export const debugUtils = {
  /**
   * Only available in development mode
   */
  logIf: (condition, message, data = null) => {
    if (isDebugMode && condition) {
      console.log(`[DEBUG-CONDITIONAL]`, message, data);
    }
  },

  /**
   * Time a function execution (development only)
   */
  timeFunction: async (name, fn) => {
    if (!isDevelopment) {
      return await fn();
    }
    
    const start = Date.now();
    const result = await fn();
    const end = Date.now();
    console.log(`[TIMING] ${name}: ${end - start}ms`);
    return result;
  },

  /**
   * Group related logs (development only)
   */
  group: (name, fn) => {
    if (!isDevelopment) {
      return fn();
    }
    
    console.group(name);
    const result = fn();
    console.groupEnd();
    return result;
  }
};

export default logger;
